import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ExclamationTriangleIcon, PlusIcon, MagnifyingGlassIcon, ArrowPathIcon, ChevronUpDownIcon,
    ChevronUpIcon, ChevronDownIcon,
} from '@heroicons/react/24/outline';
import ApiClient from '../utils/ApiClient';
import {
    deadlineTone, deadlineText, TONE_CLASSES, stageTone, TONE_BADGE,
    boardCounts, stageCounts, filterRows, sortRows, trendIndicator,
} from '../utils/sellerLegal';

const TILES = [
    { key: 'needsAction', label: 'Needs action', accent: 'text-red-600' },
    { key: 'listingNow', label: 'Listing now', accent: 'text-green-600' },
    { key: 'dueSoon', label: 'Deadlines ≤ 7 days', accent: 'text-amber-600' },
    { key: 'tracked', label: 'Tracked', accent: 'text-gray-900' },
];

const COLUMNS = [
    { key: 'seller', label: 'Seller' },
    { key: 'stage', label: 'Stage' },
    { key: 'asins', label: 'ASINs', align: 'text-right' },
    { key: 'listing', label: 'Listing' },
    { key: 'lastSeen', label: 'Last seen' },
    { key: 'deadline', label: 'Next deadline' },
];

const fmtDay = (iso) => (iso
    ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : '—');

function SortIcon({ active, dir }) {
    if (!active) return <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />;
    return dir === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />;
}

function SellerLegalTracker() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [stages, setStages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stage, setStage] = useState(null);
    const [tile, setTile] = useState(null);
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState({ key: null, dir: 'asc' });
    const [adding, setAdding] = useState(false);
    const [newId, setNewId] = useState('');
    const [newName, setNewName] = useState('');
    const [addError, setAddError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [board, meta] = await Promise.all([
                ApiClient.get('/api/seller-legal/board', { params: { includeClosed: true } }),
                ApiClient.get('/api/seller-legal/meta'),
            ]);
            setRows(board.data.flatMap((s) => s.rows));
            setStages(meta.data.stages);
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => boardCounts(rows), [rows]);
    const perStage = useMemo(() => stageCounts(rows), [rows]);
    const visible = useMemo(
        () => sortRows(filterRows(rows, { stage, tile, query }), sort.key, sort.dir),
        [rows, stage, tile, query, sort],
    );
    const openCount = rows.filter((r) => r.seller.stage !== 'CLOSED').length;

    const toggleSort = (key) => setSort((s) => {
        if (s.key !== key) return { key, dir: key === 'asins' ? 'desc' : 'asc' };
        if (s.dir === 'asc') return { key, dir: 'desc' };
        return { key: null, dir: 'asc' }; // third click: back to the default order
    });

    const addSeller = async (e) => {
        e.preventDefault();
        setAddError(null);
        try {
            const res = await ApiClient.post('/api/seller-legal/sellers', { sellerId: newId, storefrontName: newName });
            navigate(`/seller-legal-tracker/${res.data.sellerId}`);
        } catch (err) {
            setAddError(err.response?.data?.error || err.message);
        }
    };

    const pill = (value, label, n) => {
        const on = stage === value;
        return (
            <button key={value ?? 'all'} onClick={() => setStage(value)}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-sm font-medium ring-1 ring-inset ${
                        on ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
                    } ${!on && n === 0 ? 'opacity-50' : ''}`}>
                {label} <span className={on ? 'text-indigo-100' : 'text-gray-400'}>{n}</span>
            </button>
        );
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Seller Legal Tracker</h1>
                    <p className="text-sm text-gray-500">Unauthorized Schluter sellers, from detection through enforcement.</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or seller ID"
                               className="w-64 rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
                    </div>
                    <button onClick={load} title="Refresh"
                            className="rounded-md border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50">
                        <ArrowPathIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => setAdding(!adding)}
                            className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500">
                        <PlusIcon className="h-4 w-4" /> Add seller
                    </button>
                </div>
            </div>

            {adding && (
                <form onSubmit={addSeller} className="mb-5 flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow">
                    <label className="text-sm text-gray-700">Seller ID
                        <input value={newId} onChange={(e) => setNewId(e.target.value)} required placeholder="A1VS3V94W02B7O"
                               className="mt-1 block w-56 rounded-md border border-gray-300 px-2 py-1.5" />
                    </label>
                    <label className="text-sm text-gray-700">Storefront name <span className="text-gray-400">(optional — filled from offer data)</span>
                        <input value={newName} onChange={(e) => setNewName(e.target.value)}
                               className="mt-1 block w-72 rounded-md border border-gray-300 px-2 py-1.5" />
                    </label>
                    <button type="submit" className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500">Add</button>
                    <button type="button" onClick={() => setAdding(false)} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                    {addError && <p className="w-full text-sm text-red-700">{addError}</p>}
                </form>
            )}

            {/* Summary tiles — click to filter */}
            <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                {TILES.map((t) => {
                    const on = tile === t.key;
                    return (
                        <button key={t.key} onClick={() => setTile(on ? null : t.key)}
                                className={`rounded-lg bg-white p-4 text-left shadow transition hover:shadow-md ${on ? 'ring-2 ring-indigo-500' : ''}`}>
                            <div className="text-sm text-gray-500">{t.label}</div>
                            <div className={`mt-1 text-3xl font-semibold ${counts[t.key] > 0 ? t.accent : 'text-gray-300'}`}>
                                {counts[t.key]}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Stage pills */}
            <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
                {pill(null, 'All', openCount)}
                {stages.map((s) => pill(s.value, s.label, perStage[s.value] || 0))}
            </div>

            {(tile || query) && (
                <p className="mb-2 text-sm text-gray-500">
                    Showing {visible.length} of {stage ? perStage[stage] || 0 : openCount}
                    {' · '}
                    <button onClick={() => { setTile(null); setQuery(''); }} className="text-indigo-600 hover:underline">clear filters</button>
                </p>
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-lg bg-white shadow">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {COLUMNS.map((c) => (
                                <th key={c.key} scope="col"
                                    className={`px-4 py-3 font-medium text-gray-600 ${c.align || 'text-left'}`}>
                                    <button onClick={() => toggleSort(c.key)}
                                            className={`inline-flex items-center gap-1 hover:text-gray-900 ${sort.key === c.key ? 'text-gray-900' : ''}`}>
                                        {c.label} <SortIcon active={sort.key === c.key} dir={sort.dir} />
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading && (
                            <tr><td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
                        )}
                        {error && (
                            <tr><td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-red-700">{error}</td></tr>
                        )}
                        {!loading && !error && visible.length === 0 && (
                            <tr><td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-gray-500">No sellers match.</td></tr>
                        )}
                        {!loading && !error && visible.map((r) => {
                            const s = r.seller;
                            const stageLabel = stages.find((x) => x.value === s.stage)?.label || s.stage;
                            const name = s.storefrontName?.trim();
                            return (
                                <tr key={s.sellerId} onClick={() => navigate(`/seller-legal-tracker/${s.sellerId}`)}
                                    className={`cursor-pointer hover:bg-indigo-50/50 ${r.flags.length ? 'bg-red-50/40' : ''}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                            {r.flags.length > 0 && (
                                                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-600" title={r.flags.join('\n')} />
                                            )}
                                            <span className="font-medium text-gray-900">{name || s.sellerId}</span>
                                            {s.previousLastSeen && (
                                                <span title={`Returning seller — previously seen ${s.previousLastSeen}`}
                                                      className="rounded bg-blue-100 px-1.5 text-xs font-medium text-blue-800">↺ Returning</span>
                                            )}
                                        </div>
                                        <div className="mt-0.5 text-xs text-gray-500">
                                            {name ? `${s.sellerId} · ` : ''}P{s.priority}
                                            {r.flags.length > 0 && <span className="text-red-700"> · {r.flags[0]}{r.flags.length > 1 ? ` (+${r.flags.length - 1})` : ''}</span>}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONE_BADGE[stageTone(s.stage)]}`}>
                                            {stageLabel}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right tabular-nums">
                                        {(() => {
                                            const t = trendIndicator(r.trend);
                                            return (
                                                <span className="inline-flex items-baseline justify-end gap-1.5">
                                                    <span className="font-medium text-gray-900">{r.asinCount ?? 0}</span>
                                                    {t ? (
                                                        <span title={t.title} className={`inline-flex w-14 items-baseline gap-0.5 text-xs font-medium ${t.className}`}>
                                                            {t.symbol}{t.text && <span>{t.text}</span>}
                                                            {t.window && <span className="text-[10px] font-normal text-gray-400">{t.window}</span>}
                                                        </span>
                                                    ) : <span className="inline-block w-14" />}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-4 py-3">
                                        {s.active
                                            ? <span className="inline-flex items-center gap-1.5 text-green-700"><span className="h-2 w-2 rounded-full bg-green-500" />Live</span>
                                            : <span className="inline-flex items-center gap-1.5 text-gray-500"><span className="h-2 w-2 rounded-full border border-gray-400" />{s.lastSeen ? 'Gone' : 'Not seen yet'}</span>}
                                    </td>
                                    <td className="px-4 py-3 tabular-nums text-gray-700">{fmtDay(s.lastSeen)}</td>
                                    <td className={`px-4 py-3 ${TONE_CLASSES[deadlineTone(r.daysToNext)]}`}>
                                        {r.next ? deadlineText(r.next, r.daysToNext) : '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default SellerLegalTracker;
